import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { Send, Scale, Sparkles, Paperclip } from "lucide-react";

const SUGGESTIONS = [
  "Mon employeur veut me licencier, quels sont mes droits ?",
  "J'ai reçu une convocation au tribunal, que dois-je faire ?",
  "Mon propriétaire veut augmenter mon loyer, est-ce légal ?",
  "Comment contester une amende injustifiée ?",
];

export default function Chat() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [convId, setConvId] = useState(conversationId || null);
  const [docs, setDocs] = useState([]);
  const [attachedDoc, setAttachedDoc] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    setConvId(conversationId || null);
    if (conversationId) {
      api.get(`/conversations/${conversationId}`).then((r) => setMessages(r.data.messages || [])).catch(() => navigate("/chat"));
    } else {
      setMessages([]);
    }
  }, [conversationId, navigate]);

  useEffect(() => {
    api.get("/documents").then((r) => setDocs(r.data.filter((d) => d.file_type?.startsWith("image")))).catch(() => {});
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    const content = (text || input).trim();
    if (!content || streaming) return;
    setInput("");

    let cid = convId;
    if (!cid) {
      try {
        const r = await api.post("/conversations", { title: content.slice(0, 60) });
        cid = r.data.conversation_id;
        setConvId(cid);
        window.history.replaceState(null, "", `/chat/${cid}`);
      } catch {
        toast.error("Impossible de créer la conversation");
        return;
      }
    }

    setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "" }]);
    setStreaming(true);
    const docId = attachedDoc;
    setAttachedDoc(null);

    try {
      const res = await fetch(`${API}/conversations/${cid}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content, document_id: docId }),
      });
      if (!res.ok) {
        let detail = "Une erreur est survenue";
        try { detail = (await res.json()).detail || detail; } catch { /* ignore */ }
        setMessages((m) => m.slice(0, -2));
        toast.error(detail);
        if (res.status === 402) navigate("/premium");
        setStreaming(false);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const data = JSON.parse(line.slice(5).trim());
          if (data.type === "chunk") {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + data.content };
              return copy;
            });
          } else if (data.type === "error") {
            toast.error("Erreur du service IA");
          }
        }
      }
    } catch {
      toast.error("Erreur de connexion");
    } finally {
      setStreaming(false);
    }
  };

  const isEmpty = messages.length === 0;

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem-4rem)] lg:h-screen">
      <div className="border-b border-border bg-white px-5 lg:px-8 py-4 flex items-center gap-2">
        <Scale size={18} className="text-royal" />
        <h1 className="font-serif text-2xl text-navy">Assistant juridique</h1>
      </div>

      <div className="flex-1 overflow-y-auto px-5 lg:px-8 py-6 lg:py-8">
        <div className="max-w-3xl mx-auto">
          {isEmpty ? (
            <div className="pt-10">
              <div className="flex items-center gap-2 text-royal mb-3">
                <Sparkles size={18} />
                <span className="text-xs uppercase tracking-[0.2em] font-semibold">Comment puis-je vous aider ?</span>
              </div>
              <h2 className="font-serif text-4xl text-navy mb-8">Décrivez votre situation juridique</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    data-testid="suggestion-btn"
                    onClick={() => send(s)}
                    className="text-left bg-white border border-border rounded-lg p-4 text-sm text-slate-700 hover:border-royal/40 hover:shadow-sm transition-all duration-200"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {messages.map((m, i) => (
                <div key={i} data-testid={`message-${m.role}`} className="animate-fade-in-up">
                  {m.role === "user" ? (
                    <div className="flex justify-end">
                      <div className="bg-navy text-white rounded-lg rounded-br-sm px-5 py-3 max-w-[80%] text-[15px] leading-relaxed">
                        {m.content}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white border border-border rounded-lg p-6">
                      <div className="flex items-center gap-2 mb-3 text-royal">
                        <Scale size={15} />
                        <span className="text-xs font-semibold uppercase tracking-wider">Droits IA.com</span>
                      </div>
                      {m.content ? (
                        <div className="legal-memo">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                        </div>
                      ) : (
                        <div className="flex gap-1.5 py-2">
                          <span className="w-2 h-2 bg-royal/40 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                          <span className="w-2 h-2 bg-royal/40 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                          <span className="w-2 h-2 bg-royal/40 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-border bg-white px-5 lg:px-8 py-4">
        <div className="max-w-3xl mx-auto">
          {attachedDoc && (
            <div className="mb-2 inline-flex items-center gap-2 text-xs bg-royal/10 text-royal px-3 py-1.5 rounded-full">
              <Paperclip size={12} /> {docs.find((d) => d.document_id === attachedDoc)?.file_name}
              <button onClick={() => setAttachedDoc(null)} className="ml-1 font-bold">×</button>
            </div>
          )}
          <div className="flex items-end gap-2">
            {docs.length > 0 && (
              <select
                data-testid="attach-doc-select"
                value={attachedDoc || ""}
                onChange={(e) => setAttachedDoc(e.target.value || null)}
                className="border border-border rounded-md px-2 py-3 text-sm text-slate-600 max-w-[140px] bg-bone"
              >
                <option value="">📎 Document</option>
                {docs.map((d) => (
                  <option key={d.document_id} value={d.document_id}>{d.file_name}</option>
                ))}
              </select>
            )}
            <textarea
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Décrivez votre situation ou posez votre question..."
              rows={1}
              className="flex-1 resize-none border border-border rounded-md px-4 py-3 text-[15px] focus:ring-2 focus:ring-royal/40 focus:outline-none max-h-32"
            />
            <button
              data-testid="send-btn"
              onClick={() => send()}
              disabled={streaming || !input.trim()}
              className="bg-royal hover:bg-royal/90 disabled:opacity-40 text-white p-3 rounded-md transition-all duration-200"
            >
              <Send size={18} />
            </button>
          </div>
          <p className="text-[11px] text-slate-400 mt-2 text-center">Informatif uniquement · Ne remplace pas un conseil juridique personnalisé</p>
        </div>
      </div>
    </div>
  );
}
